import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { doInitLU, luHandleAppiError, type U3301InitLU } from './U3301Service';
import { U3301ContextProvider } from './U3301Context';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export const NS = 'u3301';

export const U3301ROLES = {
  READ: 'U3301:READ',
  ADD: 'U3301:ADD',
  EDIT: 'U3301:EDIT',
  DELETE: 'U3301:DELETE',
  AUDITS: 'U3301:AUDITS',
} as const;
export type U3301ROLES = (typeof U3301ROLES)[keyof typeof U3301ROLES];

const defaultInitLU: U3301InitLU = {
  launchSearch: true,
  hideSearch: false,
  nbRecordPerPage: 50,
  categoryOrigins: ['SYSTEM', 'MANUAL'],
  eventOrigins: ['MANUAL', 'LINKED'],
  categories: [],
};

const U3301: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3301InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3301'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3301InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3301InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        categories: (initLU.categories ?? []).map((c) => ({
          ...c,
          dateMode: c.dateMode ?? 'SINGLE',
          generateWeekendEvents: c.generateWeekendEvents ?? false,
        })),
        categoryOrigins: initLU.categoryOrigins ?? defaultInitLU.categoryOrigins,
        eventOrigins: initLU.eventOrigins ?? defaultInitLU.eventOrigins,
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3301ContextProvider initLU={initLUData} refreshInitLU={() => handleInitLU.getInitLU()}>
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </U3301ContextProvider>
  );
};

export default U3301;
